<script setup>
import { ref, defineProps, defineEmits, defineExpose, onMounted, onUnmounted } from 'vue'
import HtmlEditorDomTree from './HtmlEditorDomTree.vue'
import HtmlEditorInspector from './HtmlEditorInspector.vue'

defineProps({
  fileName: { type: String, default: '' },
  fileSizeHuman: { type: String, default: '' },
  editorMode: { type: String, default: 'normal' },
  modeOptions: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  textBlocksCount: { type: Number, default: 0 },
  canUndo: { type: Boolean, default: false },
  editorFrameSrcdoc: { type: String, default: '' },
  domTreeNodes: { type: Array, default: () => [] },
  selectedTreeKey: { type: String, default: '' },
  expandedTreeKeys: { type: Object, default: () => ({}) },
  selectedElementLabel: { type: String, default: '' },
  widthPx: { type: String, default: '' },
  heightPx: { type: String, default: '' },
  fontSizePx: { type: String, default: '' },
  opacityPercent: { type: String, default: '100' },
  borderRadius: { type: String, default: '0' },
  backgroundColor: { type: String, default: '#ffffff' },
  textColor: { type: String, default: '#111111' },
  textAlignMode: { type: String, default: 'none' },
  elementAlignMode: { type: String, default: 'none' },
  borderStyle: { type: String, default: 'solid' },
  marginTop: { type: String, default: '0' },
  marginRight: { type: String, default: '0' },
  marginBottom: { type: String, default: '0' },
  marginLeft: { type: String, default: '0' },
  paddingTop: { type: String, default: '0' },
  paddingRight: { type: String, default: '0' },
  paddingBottom: { type: String, default: '0' },
  paddingLeft: { type: String, default: '0' },
  borderTopWidth: { type: String, default: '0' },
  borderRightWidth: { type: String, default: '0' },
  borderBottomWidth: { type: String, default: '0' },
  borderLeftWidth: { type: String, default: '0' },
  borderTopColor: { type: String, default: '#111111' },
  borderRightColor: { type: String, default: '#111111' },
  borderBottomColor: { type: String, default: '#111111' },
  borderLeftColor: { type: String, default: '#111111' },
  formatContextMenuItems: { type: Array, default: () => [] },
  error: { type: String, default: '' },
})

const emit = defineEmits([
  'update:editorMode',
  'replace-file',
  'close-editor',
  'download-pdf',
  'repaginate',
  'frame-load',
  'tree-select',
  'tree-node-drop',
  'tree-context-action',
  'undo',
  'update:expandedTreeKeys',
  'update:widthPx',
  'update:heightPx',
  'update:fontSizePx',
  'update:opacityPercent',
  'update:borderRadius',
  'update:backgroundColor',
  'update:textColor',
  'update:borderStyle',
  'update:marginTop',
  'update:marginRight',
  'update:marginBottom',
  'update:marginLeft',
  'update:paddingTop',
  'update:paddingRight',
  'update:paddingBottom',
  'update:paddingLeft',
  'update:borderTopWidth',
  'update:borderRightWidth',
  'update:borderBottomWidth',
  'update:borderLeftWidth',
  'update:borderTopColor',
  'update:borderRightColor',
  'update:borderBottomColor',
  'update:borderLeftColor',
  'apply-layout',
  'apply-spacing',
  'apply-appearance',
  'set-element-align',
  'set-text-align',
  'remove-element',
  'reset-selection',
  'image-file-change',
])

const editorFrameRef = ref(null)
const formatContextMenuRef = ref(null)
const imageFileInput = ref(null)

function onIframeLoad() {
  emit('frame-load', editorFrameRef.value)
}

function onUndoKeydown(event) {
  if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z' || event.shiftKey) return
  event.preventDefault()
  emit('undo')
}

onMounted(() => {
  window.addEventListener('keydown', onUndoKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onUndoKeydown)
})

defineExpose({
  editorFrameRef,
  formatContextMenuRef,
  imageFileInput,
})
</script>

<template>
  <div class="editor-workbench-root">
    <header class="editor-toolbar">
      <div class="toolbar-left">
        <span class="toolbar-file">{{ fileName }}</span>
        <span v-if="fileSizeHuman" class="toolbar-meta">({{ fileSizeHuman }})</span>
        <span class="toolbar-meta">· {{ textBlocksCount }} блоков</span>
      </div>
      <div class="toolbar-center">
        <SelectButton
          :model-value="editorMode"
          :options="modeOptions"
          option-label="label"
          option-value="value"
          @update:model-value="emit('update:editorMode', $event)"
        />
        <ButtonVue
          v-if="editorMode === 'paged'"
          type="button"
          label="Перепагинировать"
          severity="secondary"
          text
          size="small"
          @click="emit('repaginate')"
        />
      </div>
      <div class="toolbar-right">
        <ButtonVue
          type="button"
          label="Отменить"
          icon="pi pi-undo"
          severity="secondary"
          text
          size="small"
          :disabled="!canUndo"
          @click="emit('undo')"
        />
        <ButtonVue type="button" label="Заменить файл" severity="secondary" text size="small" @click="emit('replace-file')" />
        <ButtonVue type="button" label="Закрыть" severity="secondary" outlined size="small" @click="emit('close-editor')" />
        <ButtonVue
          type="button"
          label="Скачать PDF"
          size="small"
          :disabled="loading"
          :loading="loading"
          @click="emit('download-pdf')"
        />
      </div>
    </header>

    <ContextMenu ref="formatContextMenuRef" :model="formatContextMenuItems" />
    <input ref="imageFileInput" type="file" class="hidden-input" accept="image/*" @change="emit('image-file-change', $event)" />

    <div class="editor-shell">
      <HtmlEditorDomTree
        :nodes="domTreeNodes"
        :selected-key="selectedTreeKey"
        :expanded-keys="expandedTreeKeys"
        @select="emit('tree-select', $event)"
        @update:expanded-keys="emit('update:expandedTreeKeys', $event)"
        @node-drop="emit('tree-node-drop', $event)"
        @context-action="emit('tree-context-action', $event)"
      />

      <div class="canvas-panel">
        <iframe
          ref="editorFrameRef"
          class="wysiwyg-frame"
          sandbox="allow-same-origin"
          :srcdoc="editorFrameSrcdoc"
          @load="onIframeLoad"
        />
      </div>

      <HtmlEditorInspector
        :selected-element-label="selectedElementLabel"
        :width-px="widthPx"
        :height-px="heightPx"
        :font-size-px="fontSizePx"
        :opacity-percent="opacityPercent"
        :border-radius="borderRadius"
        :background-color="backgroundColor"
        :text-color="textColor"
        :text-align-mode="textAlignMode"
        :element-align-mode="elementAlignMode"
        :border-style="borderStyle"
        :margin-top="marginTop"
        :margin-right="marginRight"
        :margin-bottom="marginBottom"
        :margin-left="marginLeft"
        :padding-top="paddingTop"
        :padding-right="paddingRight"
        :padding-bottom="paddingBottom"
        :padding-left="paddingLeft"
        :border-top-width="borderTopWidth"
        :border-right-width="borderRightWidth"
        :border-bottom-width="borderBottomWidth"
        :border-left-width="borderLeftWidth"
        :border-top-color="borderTopColor"
        :border-right-color="borderRightColor"
        :border-bottom-color="borderBottomColor"
        :border-left-color="borderLeftColor"
        @update:width-px="emit('update:widthPx', $event)"
        @update:height-px="emit('update:heightPx', $event)"
        @update:font-size-px="emit('update:fontSizePx', $event)"
        @update:opacity-percent="emit('update:opacityPercent', $event)"
        @update:border-radius="emit('update:borderRadius', $event)"
        @update:background-color="emit('update:backgroundColor', $event)"
        @update:text-color="emit('update:textColor', $event)"
        @update:border-style="emit('update:borderStyle', $event)"
        @update:margin-top="emit('update:marginTop', $event)"
        @update:margin-right="emit('update:marginRight', $event)"
        @update:margin-bottom="emit('update:marginBottom', $event)"
        @update:margin-left="emit('update:marginLeft', $event)"
        @update:padding-top="emit('update:paddingTop', $event)"
        @update:padding-right="emit('update:paddingRight', $event)"
        @update:padding-bottom="emit('update:paddingBottom', $event)"
        @update:padding-left="emit('update:paddingLeft', $event)"
        @update:border-top-width="emit('update:borderTopWidth', $event)"
        @update:border-right-width="emit('update:borderRightWidth', $event)"
        @update:border-bottom-width="emit('update:borderBottomWidth', $event)"
        @update:border-left-width="emit('update:borderLeftWidth', $event)"
        @update:border-top-color="emit('update:borderTopColor', $event)"
        @update:border-right-color="emit('update:borderRightColor', $event)"
        @update:border-bottom-color="emit('update:borderBottomColor', $event)"
        @update:border-left-color="emit('update:borderLeftColor', $event)"
        @apply-layout="emit('apply-layout')"
        @apply-spacing="emit('apply-spacing')"
        @apply-appearance="emit('apply-appearance')"
        @set-element-align="emit('set-element-align', $event)"
        @set-text-align="emit('set-text-align', $event)"
        @remove-element="emit('remove-element')"
        @reset-selection="emit('reset-selection')"
      />
    </div>

    <div v-if="error" class="status status-error">{{ error }}</div>
    <div v-if="editorMode === 'paged'" class="editor-note">
      Режим A4: после больших правок нажми «Перепагинировать», чтобы обновить разбиение по страницам.
    </div>
  </div>
</template>

<style scoped>
.editor-workbench-root {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - 64px);
  min-height: 0;
  background: var(--surface-ground, #f3f4f6);
}

.editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--surface-border, #e5e7eb);
  background: var(--surface-card, #ffffff);
  flex-shrink: 0;
  flex-wrap: wrap;
}

.toolbar-left,
.toolbar-center,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar-file {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color, #111827);
  word-break: break-all;
}

.toolbar-meta {
  font-size: 12px;
  color: var(--text-color-secondary, #6b7280);
}

.editor-shell {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr) 320px;
  gap: 10px;
  padding: 10px;
}

.canvas-panel {
  min-height: 0;
  min-width: 0;
  border: 1px solid var(--surface-border, #d1d5db);
  border-radius: 10px;
  background: var(--surface-card, #ffffff);
  overflow: hidden;
}

.wysiwyg-frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: var(--surface-card, #ffffff);
}

.hidden-input {
  display: none;
}

.status-error {
  margin: 0 10px 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--p-red-50, #fef2f2);
  color: var(--p-red-700, #b91c1c);
  border: 1px solid var(--p-red-200, #fecaca);
  font-size: 13px;
}

.editor-note {
  padding: 0 14px 10px;
  font-size: 12px;
  color: var(--text-color-secondary, #6b7280);
}

@media (max-width: 1100px) {
  .editor-shell {
    grid-template-columns: 1fr;
    grid-template-rows: 220px minmax(320px, 1fr) auto;
  }

  .canvas-panel {
    min-height: 320px;
  }
}
</style>
